import { Review, getInstagramShortcode, getInstagramEmbedUrl } from '@/mocks/reviews';
import StarRating from '@/components/base/StarRating';
import { useState } from 'react';

interface VideoPopupProps {
  review: Review | null;
  reviews: Review[];
  onClose: () => void;
  onNavigate: (review: Review) => void;
}

function formatPrice(price: number): string {
  return price.toLocaleString('tr-TR');
}

export default function VideoPopup({ review, reviews, onClose, onNavigate }: VideoPopupProps) {
  const [imgError, setImgError] = useState(false);

  if (!review) return null;

  const currentIndex = reviews.findIndex((r) => r.id === review.id);
  const hasPrev = currentIndex > 0;
  const hasNext = currentIndex < reviews.length - 1;

  const goPrev = () => {
    if (hasPrev) onNavigate(reviews[currentIndex - 1]);
  };

  const goNext = () => {
    if (hasNext) onNavigate(reviews[currentIndex + 1]);
  };

  const isInstagram = !!review.instagramUrl;
  const instagramShortcode = isInstagram ? getInstagramShortcode(review.instagramUrl!) : null;
  const discountPercent = review.originalPrice > 0
    ? Math.round(((review.originalPrice - review.campaignPrice) / review.originalPrice) * 100)
    : 0;

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center p-4"
      role="dialog"
      aria-modal="true"
      aria-label={`${review.customerName} yorum videosu`}
    >
      <div
        className="absolute inset-0 bg-black/70 backdrop-blur-sm"
        onClick={onClose}
      ></div>

      <div className="relative z-10 w-full max-w-4xl max-h-[90vh] flex flex-col md:flex-row bg-background-50 rounded-2xl overflow-hidden shadow-2xl animate-[fadeIn_0.25s_ease-out]">
        <button
          onClick={onClose}
          className="absolute top-3 right-3 z-20 w-8 h-8 flex items-center justify-center rounded-full bg-background-950/60 text-background-50 hover:bg-background-950/80 transition-colors cursor-pointer"
          aria-label="Kapat"
        >
          <i className="ri-close-line text-lg"></i>
        </button>

        <div className="relative w-full md:w-[45%] aspect-[9/16] md:aspect-auto bg-background-950 overflow-hidden shrink-0">
          {isInstagram && instagramShortcode ? (
            <iframe
              src={getInstagramEmbedUrl(instagramShortcode)}
              className="w-full h-full"
              frameBorder="0"
              scrolling="no"
              allowTransparency={true}
              title={`${review.customerName} - Instagram yorumu`}
              loading="lazy"
            ></iframe>
          ) : (
            <>
              {!imgError ? (
                <img
                  src={review.thumbnailUrl}
                  alt={`${review.customerName} - ${review.procedure}`}
                  className="w-full h-full object-cover object-top"
                  onError={() => setImgError(true)}
                />
              ) : (
                <div className="w-full h-full flex items-center justify-center bg-background-200">
                  <span className="w-20 h-20 flex items-center justify-center">
                    <i className="ri-video-line text-foreground-300 text-4xl"></i>
                  </span>
                </div>
              )}
              <div className="absolute inset-0 bg-black/15"></div>
              <span className="absolute inset-0 flex items-center justify-center">
                <span className="w-20 h-20 flex items-center justify-center rounded-full bg-white/90 shadow-xl cursor-pointer">
                  <i className="ri-play-fill text-primary-500 text-4xl ml-1"></i>
                </span>
              </span>
            </>
          )}
        </div>

        <div className="flex flex-col flex-1 p-5 md:p-8 overflow-y-auto">
          <div className="flex flex-col gap-3">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="inline-flex px-3 py-1 rounded-full bg-primary-50 text-xs font-medium text-primary-700 whitespace-nowrap">
                {review.procedure}
              </span>
              {isInstagram && (
                <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full bg-gradient-to-r from-pink-500 to-red-500 text-white text-xs font-medium whitespace-nowrap">
                  <span className="w-3 h-3 flex items-center justify-center">
                    <i className="ri-instagram-fill text-[10px]"></i>
                  </span>
                  Instagram
                </span>
              )}
            </div>

            <h3 className="text-xl md:text-2xl font-bold text-foreground-900 font-heading">
              {review.customerName}
            </h3>

            <div className="flex items-center gap-2">
              <StarRating rating={review.rating} size="md" />
              <span className="text-sm text-foreground-500">
                {review.rating}/5
              </span>
            </div>

            <div className="flex items-center gap-3 text-sm text-foreground-500 flex-wrap">
              <span className="inline-flex items-center gap-1">
                <span className="w-3.5 h-3.5 flex items-center justify-center">
                  <i className="ri-map-pin-line text-xs"></i>
                </span>
                {review.location}
              </span>
              <span className="inline-flex items-center gap-1">
                <span className="w-3.5 h-3.5 flex items-center justify-center">
                  <i className="ri-calendar-line text-xs"></i>
                </span>
                {new Date(review.date).toLocaleDateString('tr-TR', {
                  day: 'numeric',
                  month: 'long',
                  year: 'numeric',
                })}
              </span>
            </div>

            <div className="border-t border-background-200 my-2"></div>

            {/* Kampanya & Fiyat Karti */}
            {review.campaignDescription ? (
              <div className="flex flex-col gap-3 bg-accent-50/70 border border-accent-200/60 rounded-xl p-5">
                <div className="flex items-center gap-2">
                  <span className="w-6 h-6 flex items-center justify-center rounded-full bg-accent-500 text-background-50">
                    <i className="ri-megaphone-fill text-xs"></i>
                  </span>
                  <span className="text-xs font-semibold text-accent-700 uppercase tracking-wide">
                    {review.campaignTag}
                  </span>
                </div>
                <div className="text-sm text-foreground-700 leading-relaxed whitespace-pre-line">
                  {review.campaignDescription}
                </div>
                {review.packageIncludes.length > 0 && (
                  <ul className="flex flex-col gap-1.5 pt-1 border-t border-accent-200/40">
                    {review.packageIncludes.map((item, idx) => (
                      <li key={idx} className="flex items-start gap-2 text-sm text-foreground-700">
                        <span className="w-4 h-4 flex items-center justify-center shrink-0 mt-0.5">
                          <i className={`${idx < 2 ? 'ri-fire-fill text-accent-600' : 'ri-information-line text-accent-500'}`}></i>
                        </span>
                        <span>{item}</span>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            ) : (
              <div className="flex flex-col gap-3 bg-accent-50/70 border border-accent-200/60 rounded-xl p-4">
                <div className="flex items-center gap-2">
                  <span className="w-6 h-6 flex items-center justify-center rounded-full bg-accent-500 text-background-50">
                    <i className="ri-price-tag-3-fill text-xs"></i>
                  </span>
                  <span className="text-xs font-semibold text-accent-700 uppercase tracking-wide">
                    {review.campaignTag}
                  </span>
                </div>

                <div className="flex items-baseline gap-2 flex-wrap">
                  <span className="text-2xl md:text-3xl font-bold text-foreground-900">
                    {formatPrice(review.campaignPrice)} TL
                  </span>
                  <span className="text-sm text-foreground-400 line-through">
                    {formatPrice(review.originalPrice)} TL
                  </span>
                  <span className="inline-flex items-center px-2 py-0.5 rounded-md bg-accent-500 text-background-50 text-xs font-bold whitespace-nowrap">
                    %{discountPercent} İndirim
                  </span>
                </div>

                <div className="flex flex-col gap-1.5">
                  <span className="text-xs font-medium text-foreground-600 flex items-center gap-1.5">
                    <span className="w-3.5 h-3.5 flex items-center justify-center">
                      <i className="ri-gift-line text-[10px] text-accent-600"></i>
                    </span>
                    Paket İçeriği
                  </span>
                  <ul className="flex flex-col gap-1">
                    {review.packageIncludes.map((item, idx) => (
                      <li key={idx} className="flex items-start gap-2 text-xs text-foreground-600">
                        <span className="w-3.5 h-3.5 flex items-center justify-center shrink-0 mt-0.5">
                          <i className="ri-check-line text-[10px] text-accent-600"></i>
                        </span>
                        <span>{item}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            )}
          </div>

          <div className="mt-auto pt-5 flex flex-col gap-3">
            <div className="flex items-center justify-between">
              <button
                onClick={goPrev}
                disabled={!hasPrev}
                className={`w-9 h-9 flex items-center justify-center rounded-full border transition-colors cursor-pointer ${
                  hasPrev
                    ? 'border-foreground-300 text-foreground-600 hover:bg-background-100'
                    : 'border-background-200 text-background-300 cursor-not-allowed'
                }`}
                aria-label="Önceki yorum"
              >
                <i className="ri-arrow-left-line"></i>
              </button>
              <span className="text-xs text-foreground-400">
                {currentIndex + 1} / {reviews.length}
              </span>
              <button
                onClick={goNext}
                disabled={!hasNext}
                className={`w-9 h-9 flex items-center justify-center rounded-full transition-colors cursor-pointer ${
                  hasNext
                    ? 'bg-foreground-900 text-background-50 hover:bg-foreground-800'
                    : 'bg-background-200 text-background-400 cursor-not-allowed'
                }`}
                aria-label="Sonraki yorum"
              >
                <i className="ri-arrow-right-line"></i>
              </button>
            </div>

            <div className="flex flex-col gap-2">
              {isInstagram && instagramShortcode && (
                <a
                  href={review.instagramUrl}
                  target="_blank"
                  rel="noopener noreferrer nofollow"
                  className="flex items-center justify-center gap-2 w-full py-3 rounded-xl bg-gradient-to-r from-pink-500 via-red-500 to-yellow-500 text-white font-medium text-sm hover:opacity-90 transition-opacity duration-200 cursor-pointer whitespace-nowrap"
                >
                  <span className="w-5 h-5 flex items-center justify-center">
                    <i className="ri-instagram-fill text-lg"></i>
                  </span>
                  Instagram'da Gör
                  <span className="w-4 h-4 flex items-center justify-center">
                    <i className="ri-external-link-line text-sm"></i>
                  </span>
                </a>
              )}
              <a
                href={`https://wa.me/905433724578?text=Merhaba%2C%20${encodeURIComponent(review.procedure)}%20işlemi%20hakkında%20bilgi%20almak%20istiyorum.`}
                target="_blank"
                rel="noopener noreferrer nofollow"
                className="flex items-center justify-center gap-2 w-full py-3 rounded-xl bg-primary-500 text-background-50 font-medium text-sm hover:bg-primary-600 transition-colors duration-200 cursor-pointer whitespace-nowrap"
              >
                <span className="w-5 h-5 flex items-center justify-center">
                  <i className="ri-whatsapp-fill text-lg"></i>
                </span>
                Bu işlem hakkında bilgi al
                <span className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-arrow-right-line text-sm"></i>
                </span>
              </a>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}